import { useEffect, useState } from "react";
import { Text, Flex, CheckBox } from "../../GlobalStyles/CustomizableGlobal.style";
import { Colors } from "../../GlobalStyles/theme";
import { Container, Image } from "./home.styles";
import CheckIcon from "../../../src/assets/check.svg";
import Call from "../../../src/assets/call.svg";
import BottomNavbar from "../../Components/BottomNav";
import Products from "../../Components/Products";
import { useMutation, useQuery } from "@apollo/client";
import { CALL_ATTENDANT, GET_SHOP_SCHEMA } from "../../schemas/schemas";
import { GET_SHOP_TYPE } from "../../interfaces/shop.interface";
import { getShopId, getTagId, getOrderTableId, setCurrencyCode, getQrc } from "../../utils/getIds.utils";
import { formatImageUrl } from "../../utils/formatImage.utils";
import { titleCase } from "title-case";
import { useDispatch } from "react-redux";
import { toggleSnackbarOpen } from "../../store/slices/snackbarSlice";
import ErrorComponent from "../../Components/ErrorComponent/ErrorComponent";
import { Loader } from "../../Components/LoaderComponent/LoaderComponent";

const Home = () => {
  const { black, primaryGrey } = Colors;
  const [isAvailableProducts, setIsAvailableProducts] = useState<boolean>(true);
  const [searchString, setSearchString] = useState<string>("");
  const [shop, setShop] = useState<GET_SHOP_TYPE["getShop"]>();
  const [resolvedShopId, setResolvedShopId] = useState<string | null>(null);
  const [isPublished, setIsPublished] = useState<boolean | null>(null);
  const [showUnavailableShop, setShowUnavailableShop] = useState<boolean>(false);

  const urlSearchParams = new URLSearchParams(window.location.search);
  const qrcParam = urlSearchParams.get("qrc");
  const tagIdParam = urlSearchParams.get("tagId");
  const orderTableIdParam = urlSearchParams.get("orderTableId");
  const shopIdParam = urlSearchParams.get("shopId");

  const qrc = qrcParam || getQrc();
  const tagId = tagIdParam || getTagId();
  const orderTableId = orderTableIdParam || getOrderTableId();
  const shopId = shopIdParam || getShopId();

  const dispatch = useDispatch();

  useEffect(() => {
    if (shopIdParam) {
      setResolvedShopId(shopIdParam);
    } else if (shopId) {
      setResolvedShopId(shopId);
    }
  }, [shopIdParam, shopId]);

  const { data: shopData, loading: shopLoading } = useQuery<GET_SHOP_TYPE>(GET_SHOP_SCHEMA, {
    skip: !resolvedShopId,
    fetchPolicy: "network-only",
    variables: {
      shopId: resolvedShopId,
    },
  });

  useEffect(() => {
    if (shopData?.getShop) {
      setShop(shopData.getShop);
      setCurrencyCode(shopData.getShop.currencyCode || "NGN");
      setIsPublished(shopData.getShop.isPublished ?? null);

      if (shopData.getShop.isPublished === false) {
        setShowUnavailableShop(true);
      }
    }
  }, [shopData]);

  const [callAttendant] = useMutation<{ callAttendant: { success: boolean; message: string } }>(
    CALL_ATTENDANT,
    {}
  );

  const handleCallAttendant = async () => {
    const shopIdVal = getShopId();
    const tableIdVal = getOrderTableId();
    const tagIdVal = getTagId();

    if (!shopIdVal || (!tableIdVal && !tagIdVal)) {
      dispatch(
        toggleSnackbarOpen({
          message: "Unable to call attendant. Please refresh the page.",
          color: "ERROR",
        })
      );
      return;
    }

    try {
      const { data } = await callAttendant({
        variables: {
          shopId: shopIdVal,
          tableId: tableIdVal,
          tagId: tagIdVal,
        },
      });

      if (data?.callAttendant?.success) {
        dispatch(
          toggleSnackbarOpen({
            message: data.callAttendant.message || "You will soon be attended to...",
            color: "SUCCESS",
          })
        );
      } else {
        dispatch(
          toggleSnackbarOpen({
            message: data?.callAttendant?.message || "Failed to call attendant",
            color: "ERROR",
          })
        );
      }
    } catch (error) {
      console.error("Failed to call attendant:", error);
      dispatch(
        toggleSnackbarOpen({ message: "Failed to call attendant. Please try again.", color: "ERROR" })
      );
    }
  };

  const image = shop?.Images && shop?.Images[0]?.mediumImageOnlineURL;
  const loading = shopLoading;
  const resolvedStatus = isPublished;

  if (!qrc && !tagId && !orderTableId && !shopId && !loading) {
    return <ErrorComponent message="No QR code found. Please scan the QR code again." />;
  }

  if (showUnavailableShop && !loading) {
    return (
      <Container>
        <Flex
          alignItems="center"
          justifyContent="center"
          direction="column"
          margin="4rem 0 0 0"
          gap="1rem"
        >
          <Text fontWeight="700" fontSize="4rem" margin="0" alignSelf="center" width="90%">
            Shop unavailable
          </Text>
          <Text
            fontWeight="400"
            fontSize="1.25rem"
            margin="0"
            alignSelf="center"
            width="90%"
            textAlign="center"
          >
            This shop is currently not published or unavailable.
            <br /> Please try again later.
          </Text>
        </Flex>
      </Container>
    );
  }

  if (loading) {
    return (
      <Container>
        <Flex alignItems="center" justifyContent="center" direction="column" margin="4rem 0 0 0">
          <Loader width="80" />
        </Flex>
      </Container>
    );
  }

  return (
    <Container>
      <Flex
        style={{ gap: "1rem" }}
        alignItems="center"
        padding="0.5rem 1rem 1rem 1rem"
        maxWidth="576px"
        className="header"
      >
        <Image
          width="50px"
          height="50px"
          circular={true}
          className="shop-image"
          src={formatImageUrl(image)}
          alt=""
        />
        <Flex
          direction="column"
          height="45px"
          justifyContent="space-between"
          alignItems="flex-start"
          maxWidth="478px"
        >
          <Text color={black} fontWeight="700" margin="0">
            {shop?.shopName && titleCase(shop?.shopName)}
          </Text>
          <Flex alignItems="center" justifyContent="space-between">
            <button id="call" onClick={handleCallAttendant}>
              <img src={Call} style={{ marginRight: "5px" }} alt="call attendant" />
              Call Attendant
            </button>
            <Flex margin="0" width="60%%" alignItems="center" gap="0.5rem">
              <CheckBox
                checked={isAvailableProducts}
                onClick={() => setIsAvailableProducts(!isAvailableProducts)}
              >
                <img src={CheckIcon} alt="check icon" />
              </CheckBox>
              <label
                style={{ fontSize: "0.8rem", color: `${primaryGrey}` }}
                htmlFor="menus"
                onClick={() => setIsAvailableProducts(!isAvailableProducts)}
              >
                Available items only
              </label>
            </Flex>
          </Flex>
        </Flex>
      </Flex>

      <Products
        searchString={searchString}
        isAvailableProducts={isAvailableProducts}
        setSearchString={setSearchString}
        qrc={qrc}
      />
      <div className="bottom">
        <BottomNavbar />
      </div>
    </Container>
  );
};

export default Home;
